import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaArrowLeft, FaBell, FaChevronRight, FaClock } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import useApp from "../../hooks/useAppContext";
import { formatarDiaHora } from "../../services/conversaoTimer";
import "../adm/admPatrocinadoresList.css";
import "./notificacoes.css";

const FILTROS_TIPO = [
  { id: "todas", label: "Todas" },
  { id: "geral", label: "Aviso geral" },
  { id: "chat", label: "Mensagem" },
];

function tipoNotificacao(n) {
  return n?.comunicado?.tipo === "chat" ? "chat" : "geral";
}

const Notificacoes = () => {
  const navigate = useNavigate();
  const { notificacoesUsuario, visualizarNotificacao } = useApp();
  const marcadasRef = useRef(new Set());
  const [filtroTipo, setFiltroTipo] = useState("todas");

  const lista = useMemo(() => {
    return [...(notificacoesUsuario || [])].sort((a, b) => {
      const dataA = new Date(a?.comunicado?.created_at || a?.created_at || 0);
      const dataB = new Date(b?.comunicado?.created_at || b?.created_at || 0);
      return dataB - dataA;
    });
  }, [notificacoesUsuario]);

  const contagens = useMemo(() => {
    const base = { todas: lista.length, geral: 0, chat: 0 };
    lista.forEach((n) => {
      base[tipoNotificacao(n)] += 1;
    });
    return base;
  }, [lista]);

  const listaFiltrada = useMemo(() => {
    if (filtroTipo === "todas") return lista;
    return lista.filter((n) => tipoNotificacao(n) === filtroTipo);
  }, [lista, filtroTipo]);

  const naoLidas = useMemo(
    () => listaFiltrada.filter((n) => n.status === 0).length,
    [listaFiltrada]
  );

  useEffect(() => {
    const pendentes = lista.filter(
      (n) => n.status === 0 && !marcadasRef.current.has(n.id)
    );
    if (!pendentes.length) return;

    pendentes.forEach((n) => {
      marcadasRef.current.add(n.id);
      visualizarNotificacao(n.id);
    });
  }, [lista, visualizarNotificacao]);

  return (
    <div className="main">
      <div className="corpo-pagina notificacoes-page">
        <div className="apl-page">
          <header className="apl-header" aria-label="Cabeçalho das notificações">
            <div className="apl-header-text">
              <h1>Notificações</h1>
              <p>
                Acompanhe avisos gerais e mensagens recebidas durante o evento.
              </p>
            </div>

            <div className="apl-toolbar apl-toolbar--inline">
              <button
                type="button"
                className="apl-btn apl-btn--ghost"
                onClick={() => navigate(-1)}
                title="Voltar"
              >
                <FaArrowLeft size={14} aria-hidden />
                Voltar
              </button>
            </div>
          </header>

          <div className="body-tela-adm" style={{ paddingBottom: 0 }}>
            {lista.length > 0 ? (
              <nav className="apl-day-tabs" aria-label="Filtrar por tipo">
                {FILTROS_TIPO.map((filtro) => (
                  <button
                    key={filtro.id}
                    type="button"
                    className={`apl-day-tab${
                      filtroTipo === filtro.id ? " is-active" : ""
                    }`}
                    onClick={() => setFiltroTipo(filtro.id)}
                  >
                    {filtro.label}
                    <span className="notificacoes-filtro-count">
                      {contagens[filtro.id]}
                    </span>
                  </button>
                ))}
              </nav>
            ) : null}

            <section className="apl-card" aria-label="Lista de notificações">
              {lista.length === 0 ? (
                <div className="apl-empty">
                  <p className="apl-empty-title">Nenhuma notificação</p>
                  <p style={{ margin: 0 }}>
                    Quando houver avisos ou mensagens novas, eles aparecerão
                    aqui.
                  </p>
                </div>
              ) : listaFiltrada.length === 0 ? (
                <div className="apl-empty">
                  <p className="apl-empty-title">
                    Nenhuma notificação neste filtro
                  </p>
                  <p style={{ margin: 0 }}>
                    Selecione outro tipo para ver as demais notificações.
                  </p>
                </div>
              ) : (
                <>
                  <ul className="notificacoes-list" aria-label="Notificações">
                    {listaFiltrada.map((n) => {
                      const comunicado = n.comunicado || {};
                      const dataCriado = formatarDiaHora(
                        comunicado.created_at || n.created_at
                      );
                      const isChat = tipoNotificacao(n) === "chat";
                      const isNaoLida = n.status === 0;
                      const destinoChat = comunicado.id_usuario
                        ? `/private/chat/${comunicado.id_usuario}`
                        : null;
                      const conteudo = (
                        <>
                          <div className="notificacao-icon" aria-hidden>
                            <FaBell size={14} />
                          </div>
                          <div className="notificacao-body">
                            <div className="notificacao-top">
                              <span
                                className={`apl-tag-type${
                                  isChat ? " apl-tag-type--mm" : ""
                                }`}
                              >
                                {isChat ? "Mensagem" : "Aviso geral"}
                              </span>
                              {isNaoLida ? (
                                <span className="notificacao-badge">Nova</span>
                              ) : null}
                            </div>
                            <p className="notificacao-texto">
                              {comunicado.texto || "—"}
                            </p>
                            <div className="notificacao-meta">
                              <span>
                                <FaClock size={11} aria-hidden />
                                {dataCriado || "—"}
                              </span>
                              {isChat && destinoChat ? (
                                <span className="notificacao-cta">
                                  Abrir conversa
                                  <FaChevronRight size={10} aria-hidden />
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </>
                      );

                      return (
                        <li key={n.id}>
                          {isChat && destinoChat ? (
                            <Link
                              to={destinoChat}
                              className={`notificacao-item${
                                isNaoLida ? " is-unread" : ""
                              }`}
                            >
                              {conteudo}
                            </Link>
                          ) : (
                            <div
                              className={`notificacao-item notificacao-item--static${
                                isNaoLida ? " is-unread" : ""
                              }`}
                            >
                              {conteudo}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>

                  <footer className="apl-footer">
                    <span>
                      Exibindo <strong>{listaFiltrada.length}</strong>{" "}
                      {listaFiltrada.length === 1
                        ? "notificação"
                        : "notificações"}
                      {filtroTipo !== "todas"
                        ? ` de ${lista.length}`
                        : ""}
                    </span>
                    <span>
                      {naoLidas > 0
                        ? `${naoLidas} ${
                            naoLidas === 1 ? "não lida" : "não lidas"
                          }`
                        : "Todas lidas"}
                    </span>
                  </footer>
                </>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Notificacoes;
